import { useEffect, useState } from "react";
import axios from "axios";
import { Camera } from "lucide-react";
import { useStore } from "@/lib/store";
import { translations } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Lookbook() {
  const { lang } = useStore();
  const t = translations[lang];
  const [shots, setShots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/lookbook`)
      .then((r) => setShots(r.data.items || []))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

  const renderContent = () => {
    if (loading) {
      return <p className="text-white/40 text-center py-24 tracking-widest">···</p>;
    }
    if (shots.length === 0) {
      return (
        <div
          className="mt-12 rounded-3xl border border-dashed border-yellow-500/30 bg-yellow-500/[0.03] py-20 px-6 text-center"
          data-testid="lookbook-empty"
        >
          <Camera className="w-14 h-14 mx-auto text-yellow-400/60 mb-5" strokeWidth={1.4} />
          <p
            className="text-2xl font-black text-yellow-100 mb-3"
            style={{ fontFamily: '"Bebas Neue", system-ui', letterSpacing: "0.08em" }}
          >
            {lang === "fr" ? "BIENTÔT EN LIGNE" : "COMING SOON"}
          </p>
          <p className="text-white/55 text-sm max-w-md mx-auto">
            {lang === "fr"
              ? "Le lookbook officiel arrive très bientôt. Les premières photos seront publiées dès l'ouverture de la boutique à Conakry."
              : "The official lookbook is coming soon. First shots will be posted as soon as the Conakry store opens."}
          </p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-10 auto-rows-[240px]">
        {shots.map((s, i) => (
          <div
            key={s.id}
            data-testid={`lookbook-shot-${i}`}
            className="relative rounded-2xl overflow-hidden border border-yellow-500/15 hover:border-yellow-400/50 transition-all group"
            style={{ animation: `fadeUp 0.7s ease ${0.05 * i}s both` }}
          >
            <img
              src={resolveUrl(s.image_url)}
              alt=""
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-14" data-testid="lookbook-page">
      <p
        className="text-xs tracking-[0.5em] text-yellow-400/80 mb-3"
        style={{ fontFamily: '"JetBrains Mono", monospace' }}
      >
        {t.lookbook_eyebrow}
      </p>
      <h1
        className="text-5xl sm:text-6xl font-black"
        style={{
          fontFamily: '"Bebas Neue", system-ui',
          background: "linear-gradient(180deg, #fff 0%, #F4D77A 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {t.lookbook_title}
      </h1>
      <p className="text-white/60 mt-3 max-w-2xl">{t.lookbook_subtitle}</p>

      {renderContent()}
    </div>
  );
}
